import { useEffect, useMemo, useState } from "react";

import { TocEntry } from "./readerBody";

export interface PageTocProps {
    entries: TocEntry[];
    /** `inline`: the numbered card above the body; `side`: the "On this page" panel list with the current section. */
    variant: "inline" | "side";
    /** Accessible name of the nav. Default "On this page". */
    label?: string;
    /** A link was followed (the phone disclosure closes). */
    onNavigate?: () => void;
}

function flatten(entries: TocEntry[], out: string[] = []): string[] {
    for (const e of entries) {
        out.push(e.id);
        flatten(e.children, out);
    }
    return out;
}

/**
 * The heading the reader is at: the last one whose top passed the top of the reading area. Scroll events of any box are
 * heard on the document in the capture phase (the shell scrolls inside <main>, the reader inside its own column).
 */
function useCurrentSection(ids: string[], enabled: boolean): string | undefined {
    const [current, setCurrent] = useState<string>();
    useEffect(() => {
        if (!enabled || ids.length === 0) return;
        let frame = 0;
        const update = () => {
            frame = 0;
            let found: string | undefined;
            for (const id of ids) {
                const el = document.getElementById(id);
                if (!el) continue;
                if (el.getBoundingClientRect().top <= 120) found = id;
                else break;
            }
            setCurrent(found ?? ids[0]);
        };
        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(update);
        };
        update();
        document.addEventListener("scroll", onScroll, { capture: true, passive: true });
        return () => {
            document.removeEventListener("scroll", onScroll, { capture: true });
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [ids, enabled]);
    return current;
}

function TocList(props: { entries: TocEntry[]; current?: string; onNavigate?: () => void }) {
    return (
        <ol>
            {props.entries.map((entry) => (
                <li key={entry.id}>
                    <a
                        href={`#${entry.id}`}
                        aria-current={props.current === entry.id ? "location" : undefined}
                        onClick={(ev) => {
                            const target = document.getElementById(entry.id);
                            if (!target) return;
                            ev.preventDefault();
                            target.scrollIntoView({ behavior: "smooth", block: "start" });
                            window.history.replaceState(window.history.state, "", `#${entry.id}`);
                            props.onNavigate?.();
                        }}
                    >
                        {entry.label}
                    </a>
                    {entry.children.length ? <TocList entries={entry.children} current={props.current} onNavigate={props.onNavigate} /> : null}
                </li>
            ))}
        </ol>
    );
}

/** The page's headings as nested links (numbered inline, plain with the current section in the side panel). */
export function PageToc(props: PageTocProps) {
    const stableIds = useMemo(() => flatten(props.entries), [props.entries]);
    const current = useCurrentSection(stableIds, props.variant === "side");
    return (
        <nav className={`ark-toc is-${props.variant}`} aria-label={props.label ?? "On this page"}>
            <TocList entries={props.entries} current={props.variant === "side" ? current : undefined} onNavigate={props.onNavigate} />
        </nav>
    );
}
